/**
 * Every figure the sales dashboard shows, defined once.
 *
 * The API returns raw totals; widgets, digests and insights derive metrics
 * from them with these functions, so "average order value" can never mean one
 * thing on a KPI tile and another in an email.
 *
 * Definitions:
 *   * Net sales ("Revenue") — merchandise after discounts and refunds, as
 *     recognized. No shipping, no tax.
 *   * Gross sales — merchandise before discounts and refunds, on countable orders.
 *   * Net proceeds — net sales plus shipping collected, less channel fees. Only
 *     over channels that report fees; null when none in range does.
 *   * An order that sold lines for both sites counts once in each site's
 *     order count, and once in Combined.
 */
export const EMPTY_SALES_TOTALS = {
    netSalesMinor: 0,
    grossSalesMinor: 0,
    discountMinor: 0,
    refundedMinor: 0,
    shippingMinor: 0,
    taxMinor: 0,
    feesMinor: null,
    feeBaseNetSalesMinor: 0,
    feeBaseShippingMinor: 0,
    orders: 0,
    units: 0,
    products: 0,
    days: 0,
};
export const SALES_METRIC_IDS = [
    'net_sales',
    'gross_sales',
    'orders',
    'units',
    'aov',
    'avg_selling_price',
    'net_proceeds',
    'refunds',
    'refund_rate',
    'discount_rate',
    'fee_rate',
    'shipping',
    'units_per_day',
    'sales_per_day',
    'products_sold',
];
const ratio = (numerator, denominator) => denominator > 0 ? numerator / denominator : null;
export const SALES_METRICS = {
    net_sales: {
        id: 'net_sales',
        label: 'Net sales',
        format: 'money',
        better: 'up',
        description: 'Merchandise after discounts and refunds. Excludes shipping and tax.',
        value: (t) => t.netSalesMinor,
    },
    gross_sales: {
        id: 'gross_sales',
        label: 'Gross sales',
        format: 'money',
        better: 'up',
        description: 'Merchandise before discounts and refunds.',
        value: (t) => t.grossSalesMinor,
    },
    orders: {
        id: 'orders',
        label: 'Orders',
        format: 'count',
        better: 'up',
        description: 'Orders that count as sales.',
        value: (t) => t.orders,
    },
    units: {
        id: 'units',
        label: 'Units sold',
        format: 'count',
        better: 'up',
        description: 'Items sold on orders that count as sales.',
        value: (t) => t.units,
    },
    aov: {
        id: 'aov',
        label: 'Average order value',
        format: 'money',
        better: 'up',
        description: 'Net sales per order.',
        value: (t) => ratio(t.netSalesMinor, t.orders),
    },
    avg_selling_price: {
        id: 'avg_selling_price',
        label: 'Average selling price',
        format: 'money',
        better: 'up',
        description: 'Net sales per unit.',
        value: (t) => ratio(t.netSalesMinor, t.units),
    },
    net_proceeds: {
        id: 'net_proceeds',
        label: 'Net proceeds',
        format: 'money',
        better: 'up',
        description: 'Net sales plus shipping, less channel fees — only channels that report fees.',
        value: (t) => (t.feesMinor === null ? null : t.feeBaseNetSalesMinor + t.feeBaseShippingMinor - t.feesMinor),
    },
    refunds: {
        id: 'refunds',
        label: 'Refunds',
        format: 'money',
        better: 'down',
        description: 'Merchandise refunded on orders that count as sales.',
        value: (t) => t.refundedMinor,
    },
    refund_rate: {
        id: 'refund_rate',
        label: 'Refund rate',
        format: 'percent',
        better: 'down',
        description: 'Refunds as a share of merchandise sold after discounts.',
        value: (t) => ratio(t.refundedMinor, t.netSalesMinor + t.refundedMinor),
    },
    discount_rate: {
        id: 'discount_rate',
        label: 'Discount rate',
        format: 'percent',
        better: 'down',
        description: 'Discounts as a share of gross sales.',
        value: (t) => ratio(t.discountMinor, t.grossSalesMinor),
    },
    fee_rate: {
        id: 'fee_rate',
        label: 'Fee rate',
        format: 'percent',
        better: 'down',
        description: 'Channel fees as a share of net sales plus shipping — only channels that report fees.',
        value: (t) => (t.feesMinor === null ? null : ratio(t.feesMinor, t.feeBaseNetSalesMinor + t.feeBaseShippingMinor)),
    },
    shipping: {
        id: 'shipping',
        label: 'Shipping collected',
        format: 'money',
        better: 'up',
        description: 'Shipping charged to buyers, as recognized.',
        value: (t) => t.shippingMinor,
    },
    units_per_day: {
        id: 'units_per_day',
        label: 'Units per day',
        format: 'decimal',
        better: 'up',
        description: 'Units sold per calendar day in the range.',
        value: (t) => ratio(t.units, t.days),
    },
    sales_per_day: {
        id: 'sales_per_day',
        label: 'Net sales per day',
        format: 'money',
        better: 'up',
        description: 'Net sales per calendar day in the range.',
        value: (t) => ratio(t.netSalesMinor, t.days),
    },
    products_sold: {
        id: 'products_sold',
        label: 'Products sold',
        format: 'count',
        better: 'up',
        description: 'Distinct products with at least one sale (unlinked lines count by SKU).',
        value: (t) => t.products,
    },
};
export const isSalesMetricId = (value) => typeof value === 'string' && SALES_METRIC_IDS.includes(value);
export const metricValue = (id, totals) => SALES_METRICS[id].value(totals);
/** How a figure moved between two periods, given which way is good; null when either side has no value. */
export function changeBetween(better, current, previous) {
    if (current === null || previous === null)
        return null;
    const absolute = current - previous;
    const relative = previous !== 0 ? absolute / Math.abs(previous) : null;
    // Rates move in fractions; anything under half a percent is noise, not news.
    const flat = relative !== null ? Math.abs(relative) < 0.005 : absolute === 0;
    const up = absolute > 0;
    const sentiment = flat ? 'flat' : up === (better === 'up') ? 'good' : 'bad';
    return { absolute, relative, sentiment };
}
/** How a metric moved between two periods; null when either side has no value. */
export function metricChange(id, current, previous) {
    return changeBetween(SALES_METRICS[id].better, current, previous);
}
/**
 * Adds totals together. Orders and products are summed, so this is only right
 * for parts that share none (separate channels, not separate sites of one
 * order). Fees stay null only if every part was null.
 */
export function addTotals(a, b) {
    return {
        netSalesMinor: a.netSalesMinor + b.netSalesMinor,
        grossSalesMinor: a.grossSalesMinor + b.grossSalesMinor,
        discountMinor: a.discountMinor + b.discountMinor,
        refundedMinor: a.refundedMinor + b.refundedMinor,
        shippingMinor: a.shippingMinor + b.shippingMinor,
        taxMinor: a.taxMinor + b.taxMinor,
        feesMinor: a.feesMinor === null && b.feesMinor === null ? null : (a.feesMinor ?? 0) + (b.feesMinor ?? 0),
        feeBaseNetSalesMinor: a.feeBaseNetSalesMinor + b.feeBaseNetSalesMinor,
        feeBaseShippingMinor: a.feeBaseShippingMinor + b.feeBaseShippingMinor,
        orders: a.orders + b.orders,
        units: a.units + b.units,
        products: a.products + b.products,
        days: a.days + b.days,
    };
}
//# sourceMappingURL=sales-metrics.js.map